// ================================================================
// 導航欄組件
// ================================================================
// 版本：1.0
// 日期：2026-01-14
// 功能：頂部導航欄、使用者資訊、導航連結、登出
// ================================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Chip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import BarChartIcon from '@mui/icons-material/BarChart';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useAuth } from '../hooks/useAuth';
import { useCurrency } from '../contexts/CurrencyContext';
import CurrencySelector from './common/CurrencySelector';

/**
 * 導航欄組件
 */
const NavigationBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { formatPrice } = useCurrency();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  // 導航項目
  const navItems = [
    { label: '主頁', path: '/dashboard', icon: <DashboardIcon /> },
    { label: '問題查詢', path: '/questions', icon: <QuestionAnswerIcon /> },
    { label: '配對分析', path: '/pair', icon: <FavoriteIcon /> },
    { label: '命盤資料', path: '/chart', icon: <BarChartIcon /> },
    { label: '查詢歷史', path: '/history', icon: <HistoryIcon /> },
    { label: '交易記錄', path: '/transactions', icon: <ReceiptIcon /> }
  ];

  // 開啟使用者選單
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // 關閉使用者選單
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // 開啟手機版選單
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  // 關閉手機版選單
  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  // 導航到指定頁面
  const navigateTo = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };

  // 處理登出
  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  // 檢查是否為當前路徑
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <AppBar position="sticky" sx={{ background: 'linear-gradient(to right, #667eea, #764ba2)' }}>
      <Toolbar>
        {/* Logo / 品牌名稱 */}
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 0, fontWeight: 'bold', cursor: 'pointer', mr: 4 }}
          onClick={() => navigate('/dashboard')}
        >
          紫微斗數
        </Typography>

        {/* 桌面版導航連結 */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          {navItems.map((item) => (
            <Button
              key={item.path}
              color="inherit"
              onClick={() => navigateTo(item.path)}
              sx={{
                fontWeight: isActive(item.path) ? 'bold' : 'normal',
                backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* 手機版選單按鈕 */}
        <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }} />
        <IconButton
          color="inherit"
          sx={{ display: { xs: 'block', md: 'none' } }}
          onClick={handleMobileMenuOpen}
        >
          <MenuIcon />
        </IconButton>

        {/* 使用者資訊 & 餘額 */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* 貨幣選擇器 */}
            <CurrencySelector compact sx={{ display: { xs: 'none', sm: 'flex' } }} />

            <Chip
              label={formatPrice(user.balance)}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 'bold',
                display: { xs: 'none', sm: 'flex' }
              }}
            />

            <IconButton
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ p: 0 }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }}>
                <AccountCircleIcon />
              </Avatar>
            </IconButton>
          </Box>
        )}

        {/* 使用者下拉選單 */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          {user && (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {user.full_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                餘額: {formatPrice(user.balance)}
              </Typography>
            </Box>
          )}

          <Divider />

          <MenuItem onClick={() => { navigateTo('/profile'); handleMenuClose(); }}>
            個人資料
          </MenuItem>

          <MenuItem onClick={handleLogout}>
            <ExitToAppIcon sx={{ mr: 1 }} fontSize="small" />
            登出
          </MenuItem>
        </Menu>

        {/* 手機版導航選單 */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        >
          {navItems.map((item) => (
            <MenuItem
              key={item.path}
              onClick={() => navigateTo(item.path)}
              selected={isActive(item.path)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.icon}
                {item.label}
              </Box>
            </MenuItem>
          ))}
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default NavigationBar;
